import { useState, useEffect, useCallback } from 'react';

export const usePWA = () => {
  const [isServiceWorkerRegistered, setIsServiceWorkerRegistered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0, successCount: 0, failedCount: 0 });
  const [cacheStatus, setCacheStatus] = useState({});
  const [swError, setSwError] = useState(null);

  // Register service worker
  useEffect(() => {
    const registerServiceWorker = async () => {
      if (!('serviceWorker' in navigator)) {
        console.log('Service Worker not supported in this browser');
        setSwError('Service Worker not supported');
        return;
      }

      try {
        console.log('Attempting to register service worker...');
        
        // Check if we're in development mode
        const isDevelopment = process.env.NODE_ENV === 'development';
        
        if (isDevelopment) {
          console.log('Development mode detected - using direct cache API');
          // In development, we'll use the Cache API directly
          setIsServiceWorkerRegistered(true);
          setSwError(null);
          console.log('Direct cache API enabled for development');
          return;
        }

        // In production, try to register the service worker
        try {
          console.log('Registering service worker at: /cmm-single-build-player/service-worker.js');
          const registration = await navigator.serviceWorker.register('/cmm-single-build-player/service-worker.js');
          console.log('Service Worker registered successfully:', registration);
          
          // Enhanced registration handling for iPad compatibility
          console.log('Service Worker state:', {
            scope: registration.scope,
            installing: !!registration.installing,
            waiting: !!registration.waiting,
            active: !!registration.active,
            updatefound: false
          });
          
          // Wait for the service worker to be ready with enhanced error handling
          try {
            await navigator.serviceWorker.ready;
            console.log('Service Worker is ready');
          } catch (readyError) {
            console.warn('Service Worker ready failed:', readyError);
            // Continue anyway as this might still work
          }
          
          // Enhanced controller check with multiple retry attempts
          const checkController = (attempt = 1, maxAttempts = 5) => {
            return new Promise((resolve) => {
              const checkNow = () => {
                if (navigator.serviceWorker.controller) {
                  console.log(`✅ Service Worker is controlling the page (attempt ${attempt})`);
                  setIsServiceWorkerRegistered(true);
                  setSwError(null);
                  resolve(true);
                } else if (attempt < maxAttempts) {
                  console.log(`⚠️ Service Worker not controlling yet (attempt ${attempt}/${maxAttempts}), retrying...`);
                  setTimeout(() => {
                    checkController(attempt + 1, maxAttempts).then(resolve);
                  }, 1000 + (attempt * 500)); // Progressive delay
                } else {
                  console.log('❌ Service Worker failed to gain control after', maxAttempts, 'attempts');
                  
                  // For Chrome on iPad, try a page reload as final attempt
                  const userAgent = navigator.userAgent;
                  const isChromeOnIOS = /chrome/i.test(userAgent) && /iPad|iPhone|iPod/.test(userAgent);
                  
                  if (isChromeOnIOS && !window.sessionStorage.getItem('swReloadAttempted')) {
                    console.log('📱 Chrome on iOS detected, attempting page reload to activate service worker');
                    window.sessionStorage.setItem('swReloadAttempted', 'true');
                    window.location.reload();
                    return;
                  }
                  
                  resolve(false);
                }
              };
              
              checkNow();
            });
          };
          
          const controllerSuccess = await checkController();
          
          if (!controllerSuccess) {
            // Set up a listener for the controllerchange event as backup
            const handleControllerChange = () => {
              console.log('✅ Service Worker gained control via controllerchange event');
              setIsServiceWorkerRegistered(true);
              setSwError(null);
              navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
            };
            navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
            
            // Also check for state changes
            if (registration.installing) {
              registration.installing.addEventListener('statechange', () => {
                if (registration.installing?.state === 'activated') {
                  console.log('✅ Service Worker activated via statechange');
                  setTimeout(() => {
                    if (navigator.serviceWorker.controller) {
                      setIsServiceWorkerRegistered(true);
                      setSwError(null);
                    }
                  }, 500);
                }
              });
            }
          }
          
          // Test the service worker after a delay
          setTimeout(() => {
            testServiceWorker();
          }, 2000);
        } catch (error) {
          console.error('Failed to register service worker:', error);
          
          // Fall back to direct cache API
          console.log('Service worker registration failed, falling back to direct cache API');
          setIsServiceWorkerRegistered(true);
          setSwError('Service Worker unavailable, using direct cache API');
          
          // Set up fetch interceptor for production fallback
          setupProductionFetchInterceptor();
          
          // Test the fetch interceptor
          setTimeout(() => {
            console.log('🧪 Testing fetch interceptor in production fallback...');
            fetch('https://cmm-cloud-2.s3.us-west-1.amazonaws.com/test.mp3')
              .catch(error => {
                console.log('Expected fetch error in test:', error.message);
              });
          }, 500);
        }
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        
        // Handle specific MIME type error
        if (error.message.includes('MIME type')) {
          setSwError('Service Worker not available in development mode. Build the app for production to enable offline features.');
        } else {
          setSwError(error.message);
        }
        setIsServiceWorkerRegistered(false);
        
        // Set up fallback fetch interceptor
        setupProductionFetchInterceptor();
      }
    };



    // Set up fetch interceptor for production fallback
    const setupProductionFetchInterceptor = () => {
      // Store original fetch
      const originalFetch = window.fetch;
      
      // Override fetch to check cache first
      window.fetch = async (input, init) => {
        const url = typeof input === 'string' ? input : input.url;
        
        // Only intercept media requests
        const isMediaRequest = url.match(/\.(mp3|mp4|jpg|jpeg|png|gif|webp)$/i) || 
                              url.toLowerCase().includes('xr-chapters') || 
                              url.toLowerCase().includes('xr_scene') ||
                              url.toLowerCase().includes('xr-src');
        
        if (isMediaRequest) {
          try {
            // Try to get from cache first
            const cache = await caches.open('cmm-media-cache-v1');
            const cachedResponse = await cache.match(url);
            
            if (cachedResponse) {
              console.log('Production fallback: Serving from cache:', url);
              return cachedResponse;
            }
          } catch (error) {
            console.log('Production fallback: Cache check failed:', error);
          }
        }
        
        // Fall back to original fetch
        try {
          const response = await originalFetch(input, init);
          return response;
        } catch (error) {
          // If network fails, try cache as fallback
          if (isMediaRequest) {
            try {
              const cache = await caches.open('cmm-media-cache-v1');
              const cachedResponse = await cache.match(url);
              
              if (cachedResponse) {
                console.log('Production fallback: Network failed, serving from cache:', url);
                return cachedResponse;
              }
            } catch (cacheError) {
              console.log('Production fallback: Cache fallback failed:', cacheError);
            }
          }
          throw error;
        }
      };
    };

    // Wait for the page to load before registering
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', registerServiceWorker);
    } else {
      registerServiceWorker();
    }

    // Test service worker registration
    const testServiceWorker = async () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        console.log('✅ Service worker is controlling the page');
        
        // Test if service worker is responding
        const channel = new MessageChannel();
        channel.port1.onmessage = (event) => {
          if (event.data.type === 'PONG') {
            console.log('✅ Service worker is responding:', event.data.message);
          }
        };
        
        navigator.serviceWorker.controller.postMessage(
          { type: 'PING' },
          [channel.port2]
        );
      } else {
        console.log('❌ Service worker is NOT controlling the page');
        console.log('Service worker controller:', navigator.serviceWorker.controller);
        console.log('Service worker ready state:', navigator.serviceWorker.readyState);
      }
    };

    // Listen for messages from service worker
    const handleMessage = (event) => {
      console.log('Received message from service worker:', event.data);
      
      if (event.data.type === 'SERVICE_WORKER_READY') {
        console.log('✅ Service Worker is ready and controlling the page');
        setIsServiceWorkerRegistered(true);
        setSwError(null);
      } else if (event.data.type === 'CACHE_PROGRESS') {
        // Real-time progress updates
        setDownloadProgress({
          current: event.data.completedCount || (event.data.successCount + event.data.failedCount),
          total: event.data.total,
          successCount: event.data.successCount,
          failedCount: event.data.failedCount
        });
        
        console.log(`[PWA] Progress: ${event.data.completedCount || (event.data.successCount + event.data.failedCount)}/${event.data.total} files processed`);
      } else if (event.data.type === 'CACHE_COMPLETE') {
        // Download completion
        setDownloadProgress({
          current: event.data.total,
          total: event.data.total,
          successCount: event.data.successCount,
          failedCount: event.data.failedCount
        });
        
        console.log(`[PWA] Download complete: ${event.data.successCount} successful, ${event.data.failedCount} failed`);
        setIsDownloading(false);
        
        // Refresh cache status after download - need to get current playlist data
        // We'll trigger a re-check from the component that has access to playlistData
        setTimeout(() => {
          const event = new CustomEvent('refreshCacheStatus');
          window.dispatchEvent(event);
        }, 100);
      }
    };

    // iPad-specific service worker force check handler
    const handleForceServiceWorkerCheck = async () => {
      console.log('📱 [iPad] Force service worker check triggered');
      
      const userAgent = navigator.userAgent;
      const isIPad = /iPad/.test(userAgent);
      const isChromeOnIPad = /chrome/i.test(userAgent) && isIPad;
      
      if (isChromeOnIPad) {
        // Check current service worker state
        const hasController = !!navigator.serviceWorker?.controller;
        const isReady = navigator.serviceWorker ? await navigator.serviceWorker.ready.catch(() => null) : null;
        
        console.log('📱 [iPad] Service worker state:', {
          hasController,
          isReady: !!isReady,
          currentRegistration: isServiceWorkerRegistered
        });
        
        // If we have a controller but registration state is wrong, fix it
        if (hasController && !isServiceWorkerRegistered) {
          console.log('📱 [iPad] Fixing service worker registration state');
          setIsServiceWorkerRegistered(true);
          setSwError(null);
        }
        
        // If no controller but we should have one, try to activate
        if (!hasController && isReady) {
          console.log('📱 [iPad] Trying to activate service worker');
          try {
            // Send a message to the service worker to wake it up
            if (isReady.active) {
              isReady.active.postMessage({ type: 'PING' });
            }
          } catch (error) {
            console.log('📱 [iPad] Service worker activation error:', error);
          }
        }
        
        // If nothing works, fall back to direct cache API
        if (!hasController && !isServiceWorkerRegistered) {
          console.log('📱 [iPad] Falling back to direct cache API');
          setIsServiceWorkerRegistered(true);
          setSwError('iPad: Using direct cache API');
          
          // Set up the production fetch interceptor as fallback
          setupProductionFetchInterceptor();
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    window.addEventListener('forceServiceWorkerCheck', handleForceServiceWorkerCheck);

    return () => {
      document.removeEventListener('DOMContentLoaded', registerServiceWorker);
      navigator.serviceWorker.removeEventListener('message', handleMessage);
      window.removeEventListener('forceServiceWorkerCheck', handleForceServiceWorkerCheck);
    };
  }, []);

  // Get current tour selection based on parent page URL
  const getCurrentTourPlaylist = useCallback((playlistData) => {
    if (!playlistData || !playlistData.playlists || playlistData.playlists.length === 0) {
      console.warn('[PWA] No playlist data available');
      return null;
    }

    let selectedPlaylist = playlistData.playlists[0]; // Default
    console.log('[PWA] Default playlist:', selectedPlaylist.playlist_name);

    // Check if we can get parent page URL
    try {
      // Try to get URL from parent window (if in iframe)
      const parentUrl = window.parent !== window ? window.parent.location.href : window.location.href;
      const url = parentUrl.toLowerCase();
      console.log('[PWA] Detected URL for tour selection:', url);

      if (url.includes('dtla')) {
        console.log('[PWA] URL matches dtla');
        selectedPlaylist = playlistData.playlists.find(p => p.playlist_name === 'Ni de Aquí, Ni de Allá') || selectedPlaylist;
      } else if (url.includes('japantown')) {
        console.log('[PWA] URL matches japantown');
        selectedPlaylist = playlistData.playlists.find(p => p.playlist_name === 'Returning to the Harlem of the West') || selectedPlaylist;
      } else if (url.includes('chinatown')) {
        console.log('[PWA] URL matches chinatown');
        selectedPlaylist = playlistData.playlists.find(p => p.playlist_name === 'Look Up') || selectedPlaylist;
      } else if (url.includes('mission')) {
        console.log('[PWA] URL matches mission');
        selectedPlaylist = playlistData.playlists.find(p => p.playlist_name === 'Coffee Country') || selectedPlaylist;
      }
      console.log('[PWA] Selected playlist for download:', selectedPlaylist.playlist_name);
    } catch (error) {
      console.log('[PWA] Could not access parent URL, using default playlist:', error.message);
    }

    return selectedPlaylist;
  }, []);

  // Extract media URLs from current tour only
  const extractMediaUrls = useCallback((playlistData) => {
    const urls = new Set();
    
    // Get only the current tour's playlist
    const currentPlaylist = getCurrentTourPlaylist(playlistData);
    
    if (!currentPlaylist) {
      console.warn('[PWA] No current playlist found');
      return [];
    }

    console.log('[PWA] Extracting media URLs for tour:', currentPlaylist.playlist_name);
    
    if (currentPlaylist.tracks) {
      currentPlaylist.tracks.forEach(track => {
        if (track.audio_url) {
          urls.add(track.audio_url);
        }
        if (track.artwork_url) {
          urls.add(track.artwork_url);
        }
        // Fix XR video detection - consistent with service worker
        if (track.XR_Scene && track.XR_Scene.trim() !== '') {
          const xrUrl = track.XR_Scene.trim();
          urls.add(xrUrl);
          console.log('[PWA] Found XR Scene URL:', xrUrl);
        }
      });
    }
    
    const urlArray = Array.from(urls);
    
    // Enhanced logging for XR videos
    const xrVideos = urlArray.filter(url => {
      const lowerUrl = url.toLowerCase();
      return lowerUrl.includes('xr-chapters') || lowerUrl.includes('xr_scene') || lowerUrl.includes('xr-src');
    });
    
    console.log('[PWA] Media URL extraction for', currentPlaylist.playlist_name, ':', {
      totalUrls: urlArray.length,
      xrVideos: xrVideos.length,
      xrVideoUrls: xrVideos
    });
    
    return urlArray;
  }, [getCurrentTourPlaylist]);

  // Download media files for current tour
  const downloadAllMedia = useCallback(async (playlistData) => {
    console.log('[PWA] downloadAllMedia called, isServiceWorkerRegistered:', isServiceWorkerRegistered);
    
    // Get current tour info
    const currentPlaylist = getCurrentTourPlaylist(playlistData);
    if (!currentPlaylist) {
      console.warn('[PWA] No current playlist found for download');
      return;
    }
    
    // iPad-specific diagnostics
    const userAgent = navigator.userAgent;
    const isIPad = /iPad/.test(userAgent);
    const isChromeOnIPad = /chrome/i.test(userAgent) && isIPad;

    const urls = extractMediaUrls(playlistData);
    console.log(`[PWA] Downloading ${urls.length} media files for tour: "${currentPlaylist.playlist_name}"`);

    if (urls.length === 0) {
      console.warn(`[PWA] No media URLs found for tour: "${currentPlaylist.playlist_name}"`);
      return;
    }

    setIsDownloading(true);
    setDownloadProgress({ current: 0, total: urls.length, successCount: 0, failedCount: 0 });

    // iPad visual feedback
    if (isChromeOnIPad) {
      const statusDiv = document.getElementById('ipad-debug-status');
      if (statusDiv) {
        statusDiv.innerHTML += `<div>📥 Downloading ${urls.length} files for "${currentPlaylist.playlist_name}"...</div>`;
      }
    }

    try {
      // Try service worker first if available and registered
      if (isServiceWorkerRegistered && navigator.serviceWorker.controller) {
        console.log('[PWA] Using service worker for download');
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_MEDIA',
          urls
        });
        return; // Service worker will handle progress updates
      }
      
      // Fallback to direct cache API (for development, iPad issues, or when SW fails)
      console.log('[PWA] Using direct cache API for download');
      
      if (isChromeOnIPad) {
        const statusDiv = document.getElementById('ipad-debug-status');
        if (statusDiv) {
          statusDiv.innerHTML += '<div>🔄 Using direct cache API...</div>';
        }
      }
      
      const cache = await caches.open('cmm-media-cache-v1');
      let successCount = 0;
      let failedCount = 0;

      for (let i = 0; i < urls.length; i++) {
        try {
          const url = urls[i];
          console.log('Caching:', url);
          
          // Simple device detection
          const isIOS = /iPad|iPhone|iPod/.test(userAgent);
          
          // Fix XR video detection - consistent with service worker
          const lowerUrl = url.toLowerCase();
          const isXRVideo = lowerUrl.includes('xr-chapters') || lowerUrl.includes('xr_scene') || lowerUrl.includes('xr-src');
          
          // iPad progress feedback
          if (isChromeOnIPad) {
            const statusDiv = document.getElementById('ipad-debug-status');
            if (statusDiv) {
              const progress = Math.round(((i + 1) / urls.length) * 100);
              statusDiv.innerHTML = statusDiv.innerHTML.replace(/📥.*/, `📥 Progress: ${progress}% (${i + 1}/${urls.length})`);
            }
          }

          // Simplified fetch with shorter timeout to prevent freezing
          const FETCH_TIMEOUT = isXRVideo ? 45000 : 20000; // Reduced timeouts
          
          const fetchWithTimeout = async (fetchFunction) => {
            return Promise.race([
              fetchFunction(),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Fetch timeout')), FETCH_TIMEOUT)
              )
            ]);
          };

          let response;
          
          // Simplified fetch strategy - just two attempts
          try {
            // First attempt: Use appropriate mode for device
            const fetchMode = isIOS ? 'no-cors' : 'cors';
            console.log(`Fetching ${url} with ${fetchMode} mode`);
            
            response = await fetchWithTimeout(() => fetch(url, {
              mode: fetchMode,
              credentials: 'omit',
              cache: 'no-cache'
            }));
            
            if (response.ok || response.type === 'opaque') {
              console.log('✅ Fetch successful:', url);
            } else {
              throw new Error('Response not ok');
            }
          } catch (firstError) {
            console.log('First fetch failed, trying alternative mode:', firstError.message);
            
            // Second attempt: Try alternative mode
            const altMode = isIOS ? 'cors' : 'no-cors';
            response = await fetchWithTimeout(() => fetch(url, {
              mode: altMode,
              credentials: 'omit',
              cache: 'no-cache'
            }));
            
            if (!response.ok && response.type !== 'opaque') {
              throw new Error('Both fetch attempts failed');
            }
          }
          
          // Simple caching - try Cache API first, then IndexedDB if needed
          try {
            await cache.put(url, response.clone());
            console.log('✅ Cached successfully:', url);
            successCount++;
          } catch (cacheError) {
            console.log('Cache API failed, trying IndexedDB:', cacheError.message);
            
            // IndexedDB fallback for large files or when Cache API fails
            try {
              const arrayBuffer = await response.clone().arrayBuffer();
              const fileData = {
                url,
                data: arrayBuffer,
                headers: Object.fromEntries(response.headers.entries()),
                status: response.status,
                statusText: response.statusText,
                timestamp: Date.now()
              };
              
              const request = indexedDB.open('cmm-large-files-db', 1);
              await new Promise((resolve, reject) => {
                request.onupgradeneeded = (event) => {
                  const db = event.target.result;
                  if (!db.objectStoreNames.contains('large-files')) {
                    db.createObjectStore('large-files');
                  }
                };
                
                request.onsuccess = (event) => {
                  const db = event.target.result;
                  const transaction = db.transaction(['large-files'], 'readwrite');
                  const store = transaction.objectStore('large-files');
                  
                  const putRequest = store.put(fileData, url);
                  putRequest.onsuccess = () => resolve();
                  putRequest.onerror = () => reject(putRequest.error);
                };
                
                request.onerror = () => reject(request.error);
              });
              
              console.log('✅ Stored in IndexedDB:', url);
              successCount++;
            } catch (indexedDBError) {
              console.log('❌ Both cache methods failed for:', url, indexedDBError.message);
              failedCount++;
            }
          }
        } catch (error) {
          console.log('❌ Error caching:', urls[i], error.message);
          failedCount++;
        }

        // Update progress
        const completedCount = successCount + failedCount;
        setDownloadProgress({
          current: completedCount,
          total: urls.length,
          successCount,
          failedCount
        });
        
        console.log(`[PWA] Progress: ${completedCount}/${urls.length} files processed`);

        // Small delay to prevent overwhelming the system
        await new Promise(resolve => setTimeout(resolve, 10));
      }

      console.log(`Download complete. ${successCount} successful, ${failedCount} failed`);
      setIsDownloading(false);
      
      // iPad completion feedback
      if (isChromeOnIPad) {
        const statusDiv = document.getElementById('ipad-debug-status');
        if (statusDiv) {
          statusDiv.innerHTML += `<div>✅ Complete: ${successCount}/${urls.length} files cached</div>`;
        }
      }
      
      // Refresh cache status
      setTimeout(() => {
        const event = new CustomEvent('refreshCacheStatus');
        window.dispatchEvent(event);
      }, 100);
      
    } catch (error) {
      console.error('Error in download:', error);
      setIsDownloading(false);
      
      if (isChromeOnIPad) {
        const statusDiv = document.getElementById('ipad-debug-status');
        if (statusDiv) {
          statusDiv.innerHTML += `<div>❌ Error: ${error.message}</div>`;
        }
      }
    }
  }, [isServiceWorkerRegistered, extractMediaUrls, getCurrentTourPlaylist]);

  // Get cache status for current tour's media files
  const getCacheStatus = useCallback(async (playlistData) => {
    if (!isServiceWorkerRegistered) return;

    const currentPlaylist = getCurrentTourPlaylist(playlistData);
    if (!currentPlaylist) {
      console.warn('[PWA] No current playlist for cache status check');
      return;
    }

    const urls = extractMediaUrls(playlistData);
    if (urls.length === 0) {
      console.log('[PWA] No media URLs found for current tour:', currentPlaylist.playlist_name);
      return;
    }

    console.log('[PWA] Checking cache status for', currentPlaylist.playlist_name, '-', urls.length, 'files');

    // Check if we're in development mode or using fallback
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isUsingFallback = swError && swError.includes('direct cache API');
    
    if (isDevelopment || isUsingFallback) {
      // In development or fallback mode, check cache directly
      try {
        const cache = await caches.open('cmm-media-cache-v1');
        const status = {};
        
        for (const url of urls) {
          const response = await cache.match(url);
          status[url] = !!response;
        }
        
        setCacheStatus(status);
        return status;
      } catch (error) {
        console.error('Error checking cache status:', error);
      }
    }

    try {
      const status = await new Promise((resolve) => {
        const channel = new MessageChannel();
        channel.port1.onmessage = (event) => {
          resolve(event.data);
        };
        
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'GET_CACHE_STATUS',
            urls
          }, [channel.port2]);
        }
      });

      setCacheStatus(status);
      return status;
    } catch (error) {
      console.error('Error getting cache status:', error);
    }
  }, [isServiceWorkerRegistered, extractMediaUrls, swError, getCurrentTourPlaylist]);

  // Clear all cached media
  const clearCache = useCallback(async () => {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        const mediaCacheNames = cacheNames.filter(name => name.includes('media'));
        
        await Promise.all(
          mediaCacheNames.map(cacheName => caches.delete(cacheName))
        );
        
        console.log('Media cache cleared');
        setCacheStatus({});
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
    }
    
    // Also clear IndexedDB for large files
    if ('indexedDB' in window) {
      try {
        const request = indexedDB.deleteDatabase('cmm-large-files-db');
        request.onsuccess = () => {
          console.log('Large files database cleared');
        };
        request.onerror = (error) => {
          console.error('Error clearing large files database:', error);
        };
      } catch (error) {
        console.error('Error clearing IndexedDB:', error);
      }
    }
  }, []);

  // Get download progress percentage
  const getDownloadProgressPercentage = useCallback(() => {
    if (downloadProgress.total === 0) return 0;
    return Math.round((downloadProgress.successCount + downloadProgress.failedCount) / downloadProgress.total * 100);
  }, [downloadProgress]);

  // Check if all media is cached
  const isAllMediaCached = useCallback(() => {
    const cachedCount = Object.values(cacheStatus).filter(Boolean).length;
    const totalCount = Object.keys(cacheStatus).length;
    return totalCount > 0 && cachedCount === totalCount;
  }, [cacheStatus]);

  return {
    isServiceWorkerRegistered,
    isDownloading,
    downloadProgress,
    cacheStatus,
    downloadAllMedia,
    getCacheStatus,
    clearCache,
    getDownloadProgressPercentage,
    isAllMediaCached,
    swError
  };
}; 